import { getSessionToken } from 'API_LAYER/sessionToken'
import _ from 'lodash'
import { getFriendlyName } from 'Utils/FriendlyName'
import { getCurrentOrg, getUserInfo } from 'Utils/Helpers'
import { axios } from './axios'
import {
  DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP,
  FETCH_ALL_WHEN_ADMIN_ENDPOINTS,
  PAGINATED_ENDPOINTS,
  PUBLIC_ENDPOINTS
} from './constants'
import { buildListQueryParam, cursorWalkAll } from './cursorPaginatedGetAll'
import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'

/**
 *  @typedef {{ noNamespaceFilter?: boolean }} GetAllApiOptions
 */

/**
 *
 * @param {import('types').Endpoints} endpoint
 * @returns
 */
export function apiClient(endpoint) {
  const user = getUserInfo()
  const { tenant, apiKey } = user
  const org = getCurrentOrg()
  // Get api prefix
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'
  if (!endpoint) throw new Error('API Endpoint is null!')
  const config = {
    baseURL: `/${apiPrefix}/${tenant}/v1/${endpoint}`,
    Namespace: DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP[endpoint] ? 'default' : org,
    tenant
  }

  /**
   * @type {import('axios').AxiosRequestConfig}
   */
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      [PROCYON_SESSION_HEADER_NAME]: ''
    },
    params: {
      'ObjectMeta.Namespace': config.Namespace
    }
  }

  return {
    /**
     * @param {import('axios').AxiosRequestConfig['params']} prms
     * @returns
     */
    async getByParams(prms) {
      // TODO: re-enable — commented out for now (401s while testing locally)
      if (DISABLED_ENDPOINTS.includes(endpoint)) {
        console.debug(`[apiClient] getByParams skipped for disabled endpoint: ${endpoint}`)
        return null
      }
      let url = ''
      try {
        // Merge the params
        _.merge(axiosConfig.params, prms)
        const paramURL = new URLSearchParams(axiosConfig.params).toString()
        url = `${config.baseURL}${paramURL ? '?' + paramURL : ''}`
        // If fetch is running return
        if (FETCHING_KEYS_MAP[url]) return
        FETCHING_KEYS_MAP[url] = true
        const response = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return Object.values(response.data)[0] || null
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      } finally {
        if (url) {
          FETCHING_KEYS_MAP[url] = false
        }
      }
    },
    /**
     * @param {string} ID
     * @returns
     */
    async getByID(ID) {
      try {
        const params = {
          'ObjectMeta.ID': ID
        }
        const response = await this.getByParams(params)
        return response?.[0]
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    /**
     *
     * @param {string} name The Object Name to get
     */
    async getByName(name) {
      try {
        const r = await axios.get(`${config.baseURL}/${org}/${name}`, {
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          }
        })
        return r.data || null
      } catch (error) {}
    },
    /**
     * @param {GetAllApiOptions} options
     * @returns
     */
    async getAll(options) {
      // TODO: re-enable — commented out for now (401s while testing locally)
      // Endpoints stubbed to skip the actual GET call; every fetch path
      // (slice thunks, useReduxFetch, reduxApiClient) funnels through here.
      if (DISABLED_ENDPOINTS.includes(endpoint)) {
        console.debug(`[apiClient] getAll skipped for disabled endpoint: ${endpoint}`)
        return []
      }
      const { noNamespaceFilter = false } = options || {}
      /**
       ** Some API Endpoints (FETCH_ALL_WHEN_ADMIN_ENDPOINTS), the Namespace param ?ObjectMeta.Namespace=${config.Namespace}
       ** needs to be removed so that admin can see all the objects when in DEFAULT namespace.
       *
       */
      const isAdmin = getUserInfo()?.Spec?.UserRoles?.includes('procyon:admin')
      const isAdminRoute = FETCH_ALL_WHEN_ADMIN_ENDPOINTS[endpoint]
      // User must be admin and the endpoint is supported and, org MUST be DEFAULT
      const namespaceParam =
        noNamespaceFilter || (isAdmin && isAdminRoute && org === 'default')
          ? ''
          : `?ObjectMeta.Namespace=${config.Namespace}`

      const url = `${config.baseURL}${namespaceParam}`
      // If fetch is running return
      if (FETCHING_KEYS_MAP[url]) return
      FETCHING_KEYS_MAP[url] = true
      try {
        // Cursor-paginated endpoints (PAGINATED_ENDPOINTS) render the first page
        // early and walk the remaining pages at a larger size; every other endpoint
        // keeps the original single unpaginated GET.
        const pagination = PAGINATED_ENDPOINTS[endpoint]
        if (pagination) {
          return await this.getAllPaginated({ url, pagination, onPage: options?.onPage })
        }
        const r = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return Object.values(r.data)[0] || []
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return []
      } finally {
        if (url) {
          FETCHING_KEYS_MAP[url] = false
        }
      }
    },
    /**
     * Cursor walk for endpoints in PAGINATED_ENDPOINTS. Shared logic lives in
     * cursorPaginatedGetAll.js. Pages are delivered via onPage() as they arrive
     * so the UI can render progressively; the resolved value is every row.
     * @param {{ url: string, pagination: { firstPageSize: number, walkPageSize: number, maxPages?: number }, onPage?: (pageRows: any[], totalSoFar: number) => void }} param0
     * @returns {Promise<any[]>}
     */
    async getAllPaginated({ url, pagination, onPage }) {
      const fetchPage = async (queryParamValue) => {
        const r = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: { 'ObjectMeta.QueryParam': queryParamValue }
        })
        return Object.values(r.data)[0] || []
      }
      return cursorWalkAll({ fetchPage, ...pagination, onPage })
    },
    async create(object) {
      try {
        object.ObjectMeta.Name = object.ObjectMeta.Name || getFriendlyName()
        object.ObjectMeta.Namespace = object.ObjectMeta.Namespace || config.Namespace
        object.ObjectMeta.Tenant = config.tenant
        const response = await axios.post(config.baseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        throw error
      }
    },
    async update(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        const response = await axios.put(objectBaseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async updateOrThrow(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        const response = await axios.put(objectBaseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {}
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        throw error
      }
    },
    async delete(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        await axios.delete(objectBaseURL, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        /**
         * * Backend response for delete doesn't contain info on which object got deleted.
         * * Hence, we return the `object` as is.
         *  */
        return object
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async deleteOrThrow(object) {
      const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
      const response = await axios.delete(objectBaseURL, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
        },
        params: {}
      })
      if (response?.data?.code && response.data.code !== 0) {
        throw new Error(response.data.error || response.data.message || 'Unknown error')
      }
      return object
    },
    /**
     * Single QueryParam page fetch. NOTE: the backend ignores the offset value
     * (on-switch only) and pages via the page_state cursor — startIndex cannot
     * skip ahead. Kept for the existing paginatedFetchThunk signature.
     * @param {number} startIndex ignored by the backend
     * @param {number} endIndex used as the page limit
     * @returns {Promise<any[]|null>}
     */
    async getAllByOffset(startIndex, endIndex) {
      try {
        const url = `${config.baseURL}?ObjectMeta.Namespace=${config.Namespace}`
        const response = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {
            'ObjectMeta.QueryParam': buildListQueryParam({ limit: endIndex })
          }
        })
        return Object.values(response.data)[0] || null
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    }
  }
}

/**
 * Map which stores GET url endpoints which is running, to prevent duplicate api calls
 */
const FETCHING_KEYS_MAP = {}

// TODO: re-enable — commented out for now (401s while testing locally)
// githubresources was removed from this list: the endpoint was renamed to
// scmresources on the backend (githubaccount.proto -> scm_account.proto,
// githubresource.proto -> scm_resource.proto, Suman 2026-07). The 401s were
// the old endpoint no longer existing.
const DISABLED_ENDPOINTS = ['workloadidentitys']

const createObjectBaseURL = (object, { endpoint, tenant }) => {
  const name = object.ObjectMeta.Name
  const org = object.ObjectMeta.Namespace
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'

  return `/${apiPrefix}/${tenant}/v1/${endpoint}/${org}/${name}`
}
